import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { Plus, Check, X, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLOR = {
  Pending: 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700',
};

const fmt = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

export default function ApprovalsCenter() {
  const [tab, setTab] = useState('regularization');
  const [regs, setRegs] = useState([]);
  const [ot, setOt] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('Pending');

  const [regForm, setRegForm] = useState({ employee_id: '', date: '', check_in: '', check_out: '', reason: '', request_type: 'Missed Punch' });
  const [otForm, setOtForm] = useState({ employee_id: '', date: '', hours: 1, project_id: '', remarks: '' });

  const load = async () => {
    try {
      const [r, o, e] = await Promise.all([
        apiGet(`/hr/regularization?status=${filterStatus === 'All' ? '' : filterStatus}`),
        apiGet(`/hr/overtime?status=${filterStatus === 'All' ? '' : filterStatus}`),
        apiGet('/hr/employees'),
      ]);
      setRegs(r.requests || []);
      setOt(o.overtime || []);
      setEmployees(e);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, [filterStatus]);

  const submitReg = async (e) => {
    e.preventDefault();
    try { await apiPost('/hr/regularization', regForm); toast.success('Request submitted'); setShowForm(false); setRegForm({ employee_id: '', date: '', check_in: '', check_out: '', reason: '', request_type: 'Missed Punch' }); load(); }
    catch (err) { toast.error('Failed'); }
  };
  const submitOt = async (e) => {
    e.preventDefault();
    try { await apiPost('/hr/overtime', { ...otForm, hours: parseFloat(otForm.hours) }); toast.success('OT submitted'); setShowForm(false); setOtForm({ employee_id: '', date: '', hours: 1, project_id: '', remarks: '' }); load(); }
    catch (err) { toast.error('Failed'); }
  };
  const approveReg = async (id) => { try { await apiPut(`/hr/regularization/${id}/approve`); toast.success('Approved'); load(); } catch (err) { toast.error('Failed'); } };
  const rejectReg = async (id) => { try { await apiPut(`/hr/regularization/${id}/reject`); toast.success('Rejected'); load(); } catch (err) { toast.error('Failed'); } };
  const approveOt = async (id) => { try { await apiPut(`/hr/overtime/${id}/approve`); toast.success('Approved'); load(); } catch (err) { toast.error('Failed'); } };
  const rejectOt = async (id) => { try { await apiPut(`/hr/overtime/${id}/reject`); toast.success('Rejected'); load(); } catch (err) { toast.error('Failed'); } };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Approvals Center</h1>
          <p className="text-sm text-slate-600 mt-1">Attendance regularization & overtime requests awaiting your action.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-request-btn">
          <Plus size={16} /> New {tab === 'regularization' ? 'Regularization' : 'Overtime'}
        </button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="border-b border-slate-200 flex gap-1">
          {['regularization', 'overtime'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-display font-semibold transition-colors border-b-2 ${tab === t ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-600 hover:text-[#0F172A]'}`} data-testid={`tab-${t}`}>
              {t === 'regularization' ? `Regularization (${regs.length})` : `Overtime (${ot.length})`}
            </button>
          ))}
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input text-xs w-32">
          <option>Pending</option><option>Approved</option><option>Rejected</option><option>All</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {tab === 'regularization' ? (
          <table className="w-full dense-table">
            <thead><tr><th>Employee</th><th>Date</th><th>Type</th><th>In/Out</th><th>Reason</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {regs.map(r => (
                <tr key={r.id}>
                  <td><div className="font-display font-semibold">{r.employee_name}</div><div className="text-xs text-slate-500">{r.emp_code}</div></td>
                  <td className="text-xs">{r.date}</td>
                  <td className="text-xs">{r.request_type}</td>
                  <td className="text-xs">{r.check_in || '—'} / {r.check_out || '—'}</td>
                  <td className="text-xs max-w-xs truncate">{r.reason}</td>
                  <td><span className={`tag ${STATUS_COLOR[r.status]}`}>{r.status}</span></td>
                  <td>
                    {r.status === 'Pending' && (
                      <div className="flex gap-1">
                        <button onClick={() => approveReg(r.id)} className="w-7 h-7 flex items-center justify-center bg-emerald-50 text-emerald-700 rounded-sm hover:bg-emerald-100" data-testid={`approve-reg-${r.id}`}><Check size={14} /></button>
                        <button onClick={() => rejectReg(r.id)} className="w-7 h-7 flex items-center justify-center bg-red-50 text-red-700 rounded-sm hover:bg-red-100"><X size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!regs.length && <tr><td colSpan="7" className="p-8 text-center text-sm text-slate-500">No regularization requests.</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="w-full dense-table">
            <thead><tr><th>Employee</th><th>Date</th><th>Hours</th><th>Project</th><th>Remarks</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {ot.map(r => (
                <tr key={r.id}>
                  <td><div className="font-display font-semibold">{r.employee_name}</div><div className="text-xs text-slate-500">{r.emp_code}</div></td>
                  <td className="text-xs">{r.date}</td>
                  <td className="font-display font-bold">{r.hours}h</td>
                  <td className="text-xs">{r.project_id || '—'}</td>
                  <td className="text-xs max-w-xs truncate">{r.remarks}</td>
                  <td><span className={`tag ${STATUS_COLOR[r.status]}`}>{r.status}</span></td>
                  <td>
                    {r.status === 'Pending' && (
                      <div className="flex gap-1">
                        <button onClick={() => approveOt(r.id)} className="w-7 h-7 flex items-center justify-center bg-emerald-50 text-emerald-700 rounded-sm hover:bg-emerald-100" data-testid={`approve-ot-${r.id}`}><Check size={14} /></button>
                        <button onClick={() => rejectOt(r.id)} className="w-7 h-7 flex items-center justify-center bg-red-50 text-red-700 rounded-sm hover:bg-red-100"><X size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!ot.length && <tr><td colSpan="7" className="p-8 text-center text-sm text-slate-500">No overtime entries.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">New {tab === 'regularization' ? 'Regularization' : 'Overtime'}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            {tab === 'regularization' ? (
              <form onSubmit={submitReg} className="p-5 space-y-4">
                <div>
                  <label className="label">Employee *</label>
                  <select value={regForm.employee_id} onChange={(e) => setRegForm({ ...regForm, employee_id: e.target.value })} className="input" required>
                    <option value="">Select</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} — ${e.name}`}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Date *</label><input type="date" value={regForm.date} onChange={(e) => setRegForm({ ...regForm, date: e.target.value })} className="input" required /></div>
                  <div>
                    <label className="label">Type</label>
                    <select value={regForm.request_type} onChange={(e) => setRegForm({ ...regForm, request_type: e.target.value })} className="input">
                      <option>Missed Punch</option><option>Wrong Punch</option><option>Forgot</option>
                    </select>
                  </div>
                  <div><label className="label">Check In</label><input type="time" value={regForm.check_in} onChange={(e) => setRegForm({ ...regForm, check_in: e.target.value })} className="input" /></div>
                  <div><label className="label">Check Out</label><input type="time" value={regForm.check_out} onChange={(e) => setRegForm({ ...regForm, check_out: e.target.value })} className="input" /></div>
                </div>
                <div><label className="label">Reason *</label><textarea value={regForm.reason} onChange={(e) => setRegForm({ ...regForm, reason: e.target.value })} className="input" rows={3} required /></div>
                <div className="flex gap-2 justify-end pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                  <button type="submit" className="btn-primary">Submit</button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitOt} className="p-5 space-y-4">
                <div>
                  <label className="label">Employee *</label>
                  <select value={otForm.employee_id} onChange={(e) => setOtForm({ ...otForm, employee_id: e.target.value })} className="input" required>
                    <option value="">Select</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} — ${e.name}`}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Date *</label><input type="date" value={otForm.date} onChange={(e) => setOtForm({ ...otForm, date: e.target.value })} className="input" required /></div>
                  <div><label className="label">Hours *</label><input type="number" min="0.5" max="24" step="0.5" value={otForm.hours} onChange={(e) => setOtForm({ ...otForm, hours: e.target.value })} className="input" required /></div>
                </div>
                <div><label className="label">Project (optional)</label><input type="text" value={otForm.project_id} onChange={(e) => setOtForm({ ...otForm, project_id: e.target.value })} className="input" /></div>
                <div><label className="label">Remarks</label><textarea value={otForm.remarks} onChange={(e) => setOtForm({ ...otForm, remarks: e.target.value })} className="input" rows={3} /></div>
                <div className="flex gap-2 justify-end pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                  <button type="submit" className="btn-primary">Submit</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
